import { faGear as faGearRegular, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { faGear as faGearSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Divider, Flex } from '@modusign/design-system';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { useMount, useToggle } from 'react-use';

import { HamburgerButton } from '@app/components/Buttons';
import ModusignLogo from '@app/components/Logos/ModusignLogo';
import MyProfile from '@app/components/MyProfile';
import { useGNBFoldHandle } from '@app/containers/GlobalNavigationBar/GNBFoldHandleContext';
import GNBLinkButtonIcon from '@app/containers/GlobalNavigationBar/GNBLinkButtonIcon';
import GNBFoldHandler from '@app/containers/GlobalNavigationBar/GNBSubHeader/GNBFoldHandler';
import HomeSection from '@app/containers/GlobalNavigationBar/HomeSection';
import { useColorScheme } from '@app/hooks/useColorScheme';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { loadAccount } from '@app/store/account/actions';

import DocumentManagementSection from './DocumentManagementSection';
import DraftSection from './DraftSection';
import GlobalNavigationBarMenu from './GlobalNavigationBarMenu';
import GNBSubHeader from './GNBSubHeader';
import SignatureSection from './SignatureSection';

function GlobalNavigationBar() {
  const dispatch = useDispatch();

  const { theme, colorScheme } = useColorScheme();

  const [isGNBOpen, toggleGNBOpen] = useToggle(false);
  const [hasMenuSectionScrolled, setHasMenuSectionScrolled] = useState(false);

  useEffectOnPushState(() => toggleGNBOpen(false), []);

  useMount(() => {
    dispatch(loadAccount());
  });

  const isMobileWidth = useResponsiveValue([true, true, false]);
  useEffect(() => {
    if (isGNBOpen && isMobileWidth) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isGNBOpen, isMobileWidth]);

  return (
    <>
      <Box
        display={[isGNBOpen ? 'block' : 'none', null, 'none']}
        height="100vh"
        position="fixed"
        sx={{
          transitionDelay: '0s, 0.2s',
          transitionDuration: '0.2s, 0s',
          transitionProperty: 'opacity, left',
        }}
        top="0"
        width="100%"
        zIndex={500}
      />

      <GNBContainer isGNBOpen={isGNBOpen}>
        <GNBHeader>
          <Flex alignItems="center" justifyContent="center">
            <Box display={['block', null, 'none']} mr={3}>
              <HamburgerButton
                active={isGNBOpen}
                rowStyle={{ background: colorScheme === 'default' ? 'dimgray' : 'gray' }}
                onClick={toggleGNBOpen}
              />
            </Box>
            <Link to="/">
              <ModusignLogo color={theme.gnb.logoColor} width={[110, 116]} />
            </Link>
          </Flex>
          <Flex alignItems="center" display={['flex', null, 'none']} sx={{ gap: 16 }}>
            <Box
              as={FontAwesomeIcon}
              className="tagContactButton"
              color="iconNeutralLight"
              height="22px"
              icon={faQuestionCircle}
              width="22px"
            />
            <MyProfile />
          </Flex>
        </GNBHeader>
        <GNBSubHeader hasMenuSectionScrolled={hasMenuSectionScrolled} />
        <GNBMenuSectionContainer onScrollFromTop={setHasMenuSectionScrolled}>
          <HomeSection />
          <GNBDivider />
          <DocumentManagementSection />
          <GNBDivider />
          <SignatureSection />
          <GNBDivider />
          <DraftSection />
          <GNBDivider />
          <GlobalNavigationBarMenu
            leftIcon={<GNBLinkButtonIcon icon={faGearRegular} />}
            leftIconActive={<GNBLinkButtonIcon icon={faGearSolid} />}
            to="/settings/profile"
          >
            설정
          </GlobalNavigationBarMenu>
        </GNBMenuSectionContainer>
      </GNBContainer>
    </>
  );
}

const useEffectOnPushState: typeof useEffect = (effect, deps) => {
  const history = useHistory();

  useEffect(() => history.listen(effect), [effect, deps, history]);
};

function GNBContainer({ children, isGNBOpen }: { children: ReactNode; isGNBOpen: boolean }) {
  const { theme } = useColorScheme();
  const { folded } = useGNBFoldHandle();

  const isMobileWidth = useResponsiveValue([true, true, false]);

  const [isFoldHandlerVisible, setIsFoldHandlerVisible] = useState(false);

  return (
    <Flex
      backgroundColor={theme.gnb.background}
      borderRight="1px solid"
      borderRightColor={theme.gnb.dividerColor}
      data-testid="globalNavigationBar"
      flexDirection="column"
      height={['auto', null, '100vh']}
      left="0"
      maxHeight={[isGNBOpen ? '100vh' : '60px', null, '100vh']}
      overflow={['hidden', null, 'visible']}
      position="fixed"
      sx={{
        transitionDuration: '0.2s',
        transitionProperty: 'max-height',
        transitionTimingFunction: 'cubic-bezier(0.3, 0.6, 0.6, 1)',
      }}
      top="0"
      width={['100%', null, folded ? '84px' : '200px']}
      zIndex={500}
      onMouseEnter={() => {
        if (isMobileWidth) {
          return;
        }
        setIsFoldHandlerVisible(true);
      }}
      onMouseLeave={() => setIsFoldHandlerVisible(false)}
    >
      {isFoldHandlerVisible && <GNBFoldHandler />}
      {children}
    </Flex>
  );
}

function GNBHeader({ children }: { children: ReactNode }) {
  const { theme } = useColorScheme();

  return (
    <Flex
      alignItems="center"
      as="section"
      borderBottom="1px solid"
      borderBottomColor={theme.gnb.dividerColor}
      flex="none"
      height={60}
      justifyContent={['space-between', null, 'center']}
      p={4}
      textAlign={['left', null, 'center']}
      width="100%"
    >
      {children}
    </Flex>
  );
}

function GNBMenuSectionContainer({
  children,
  onScrollFromTop,
}: {
  children: ReactNode;
  onScrollFromTop: (hasDetached: boolean) => void;
}) {
  const menuListRef = useRef<HTMLDivElement>(null);

  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useMount(() => {
    if (menuListRef.current) {
      setScrollbarWidth(menuListRef.current.offsetWidth - menuListRef.current.clientWidth);
    }
  });

  return (
    <Box
      height="auto"
      overflowX="hidden"
      overflowY="scroll"
      pb={[160, null, 50]}
      pl="14px"
      pr={`${14 - scrollbarWidth}px`}
      pt="10px"
      ref={menuListRef}
      sx={{
        overscrollBehaviorY: 'contain',
        scrollbarColor: 'transparent transparent',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          background: 'transparent',
          width: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '9999px',
          backgroundClip: 'padding-box',
          borderColor: 'transparent',
          borderStyle: 'solid',
          borderWidth: '2px',
        },
        '&:hover': {
          scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          },
        },
      }}
      onScroll={() => menuListRef.current && onScrollFromTop(menuListRef.current?.scrollTop > 0)}
    >
      {children}
    </Box>
  );
}

function GNBDivider() {
  const { theme } = useColorScheme();

  return (
    <Box px={3} py="10px">
      <Divider bg={theme.gnb.dividerColor} />
    </Box>
  );
}

export default GlobalNavigationBar;
